/**
 * Creates a WAV file from an AudioBuffer
 *
 * This function converts Web Audio API AudioBuffer data into a standard WAV format
 * that can be saved as a file or transmitted over the network. The function generates
 * a complete WAV file with proper headers including RIFF chunk, format chunk, and data chunk.
 *
 * @param audioBuffer - The AudioBuffer to convert to WAV format. This should contain
 *                      audio data from the Web Audio API (e.g., from MediaRecorder, AudioContext, etc.)
 *
 * @returns An ArrayBuffer containing the complete WAV file data with headers and audio samples.
 *          The audio is encoded as 16-bit PCM at the original sample rate and channel count.
 *
 * @example
 * ```typescript
 * // Convert an AudioBuffer to WAV format
 * const audioBuffer = audioContext.createBuffer(2, 44100, 44100); // 2 channels, 44.1kHz, 1 second
 * const wavBuffer = createWAVFromAudioBuffer(audioBuffer);
 *
 * // Create a Blob for file download
 * const blob = new Blob([wavBuffer], { type: 'audio/wav' });
 * const url = URL.createObjectURL(blob);
 *
 * // Or use directly with other APIs
 * const audioData = new Uint8Array(wavBuffer);
 * ```
 *
 * @remarks
 * - The function generates standard WAV format with 16-bit PCM encoding
 * - Supports any number of channels (mono, stereo, surround, etc.)
 * - Preserves the original sample rate from the AudioBuffer
 * - Output is little-endian as per WAV specification
 * - Header size is always 44 bytes + audio data
 *
 * @see {@link https://en.wikipedia.org/wiki/WAV|WAV file format specification}
 */
export const createWAVFromAudioBuffer = (audioBuffer: AudioBuffer): ArrayBuffer => {
	// Extract audio properties from the buffer
	const numChannels = audioBuffer.numberOfChannels; // Mono = 1, Stereo = 2, etc.
	const sampleRate = audioBuffer.sampleRate; // Samples per second (e.g., 44100 Hz)
	const length = audioBuffer.length; // Total number of samples

	// Calculate buffer size: 44 bytes for WAV header + audio data
	// Each sample is 16-bit (2 bytes), multiplied by number of channels
	const buffer = new ArrayBuffer(44 + length * numChannels * 2);
	const view = new DataView(buffer);

	/**
	 * Helper function to write ASCII strings to the buffer
	 * WAV files use ASCII strings for chunk identifiers
	 */
	const writeString = (offset: number, string: string) => {
		for (let i = 0; i < string.length; i++) {
			view.setUint8(offset + i, string.charCodeAt(i));
		}
	};

	// Write WAV file header (44 bytes total)
	// RIFF chunk identifier - indicates this is a RIFF-formatted file
	writeString(0, 'RIFF');

	// File size minus 8 bytes (RIFF identifier + size field)
	// 36 bytes for remaining header + audio data size
	view.setUint32(4, 36 + length * numChannels * 2, true); // true = little-endian

	// WAVE format identifier
	writeString(8, 'WAVE');

	// Format chunk identifier
	writeString(12, 'fmt ');

	// Format chunk size (16 bytes for PCM format)
	view.setUint32(16, 16, true);

	// Audio format (1 = PCM/uncompressed)
	view.setUint16(20, 1, true);

	// Number of channels (1 = mono, 2 = stereo, etc.)
	view.setUint16(22, numChannels, true);

	// Sample rate in Hz (e.g., 44100, 48000)
	view.setUint32(24, sampleRate, true);

	// Byte rate: sample rate * number of channels * bytes per sample
	view.setUint32(28, sampleRate * numChannels * 2, true);

	// Block align: number of channels * bytes per sample
	view.setUint16(32, numChannels * 2, true);

	// Bits per sample (16-bit audio)
	view.setUint16(34, 16, true);

	// Data chunk identifier
	writeString(36, 'data');

	// Data chunk size: total audio data size in bytes
	view.setUint32(40, length * numChannels * 2, true);

	// Write audio data starting at byte 44 (after header)
	let offset = 44;

	// Iterate through each sample in the audio buffer
	for (let i = 0; i < length; i++) {
		// For each sample, write data from all channels
		for (let channel = 0; channel < numChannels; channel++) {
			// Get the sample value for this channel and sample index
			// Clamp the value between -1 and 1 (Web Audio API range)
			const sample = Math.max(-1, Math.min(1, audioBuffer.getChannelData(channel)[i]));

			// Convert float sample (-1 to 1) to 16-bit integer
			// For negative values: multiply by 0x8000 (32768)
			// For positive values: multiply by 0x7fff (32767)
			// This ensures proper 16-bit signed integer representation
			view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);

			// Move to next sample position (2 bytes per sample)
			offset += 2;
		}
	}

	return buffer;
};
